/**
 * Chart Component
 * Recharts wrapper for consistent chart styling
 */

import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { cn } from '@/lib/utils'

// Chart colors
export const chartColors = {
  primary: '#0066cc',
  success: '#059669',
  warning: '#f59e0b',
  danger: '#dc2626',
  info: '#3b82f6',
  muted: '#8a94a6',
}

// Custom tooltip
export function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-lg">
      <p className="text-sm font-medium text-foreground">{label}</p>
      {payload.map((entry: any, index: number) => (
        <p key={index} className="text-sm text-muted-foreground">
          <span className="font-medium" style={{ color: entry.color }}>
            {entry.name}:
          </span>{' '}
          {entry.value}
        </p>
      ))}
    </div>
  )
}

// Line Chart Component
interface LineChartProps {
  data: any[]
  lines: { dataKey: string; name: string; color?: string; strokeWidth?: number }[]
  xAxisKey: string
  height?: number
  className?: string
  area?: boolean
  grid?: boolean
  legend?: boolean
}

export function TradingLineChart({
  data,
  lines,
  xAxisKey,
  height = 300,
  className,
  area = false,
  grid = true,
  legend = false,
}: LineChartProps) {
  const ChartComponent = area ? AreaChart : LineChart
  const DataComponent = area ? Area : Line

  return (
    <div className={cn('w-full', className)}>
      <ResponsiveContainer width="100%" height={height}>
        <ChartComponent data={data}>
          {grid && <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />}
          <XAxis
            dataKey={xAxisKey}
            className="text-xs text-muted-foreground"
            stroke="hsl(var(--muted-foreground))"
          />
          <YAxis className="text-xs text-muted-foreground" stroke="hsl(var(--muted-foreground))" />
          <Tooltip content={<CustomTooltip />} />
          {legend && <Legend />}
          {lines.map((line) => (
            <DataComponent
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color || chartColors.primary}
              strokeWidth={line.strokeWidth || 2}
              fill={area ? line.color || chartColors.primary : undefined}
              fillOpacity={area ? 0.2 : undefined}
            />
          ))}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  )
}

// Bar Chart Component
interface BarChartProps {
  data: any[]
  bars: { dataKey: string; name: string; color?: string }[]
  xAxisKey: string
  horizontal?: boolean
  height?: number
  className?: string
  stacked?: boolean
}

export function TradingBarChart({
  data,
  bars,
  xAxisKey,
  horizontal = false,
  height = 300,
  className,
  stacked = false,
}: BarChartProps) {
  return (
    <div className={cn('w-full', className)}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout={horizontal ? 'horizontal' : 'vertical'}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey={horizontal ? undefined : xAxisKey}
            className="text-xs text-muted-foreground"
            stroke="hsl(var(--muted-foreground))"
          />
          <YAxis
            type={horizontal ? 'category' : 'number'}
            dataKey={horizontal ? xAxisKey : undefined}
            className="text-xs text-muted-foreground"
            stroke="hsl(var(--muted-foreground))"
          />
          <Tooltip content={<CustomTooltip />} />
          {bars.map((bar) => (
            <Bar
              key={bar.dataKey}
              dataKey={bar.dataKey}
              name={bar.name}
              fill={bar.color || chartColors.primary}
              stackId={stacked ? 'stack' : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// Donut/Pie Chart Component
interface PieChartProps {
  data: { name: string; value: number; color?: string }[]
  height?: number
  className?: string
  innerRadius?: number
  outerRadius?: number
  showLabels?: boolean
}

export function TradingPieChart({
  data,
  height = 300,
  className,
  innerRadius = 60,
  outerRadius = 80,
  showLabels = true,
}: PieChartProps) {
  const COLORS = [chartColors.primary, chartColors.success, chartColors.warning, chartColors.danger, chartColors.info]

  return (
    <div className={cn('w-full', className)}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            dataKey="value"
            label={showLabels}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}
